import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, CheckCircle2, AlertCircle, Lock, SlidersHorizontal, Plus, Trash2 } from 'lucide-react';
import {
    useGetInvestigationMilestonesQuery,
    useSaveMilestoneDraftMutation,
    useSubmitMilestoneMutation,
    useRemoveMilestoneSectionMutation,
    useAddMilestoneSectionMutation,
} from '@/store/api/apiSlice';
import type {
    ChecklistFinding,
    InvestigationMilestone,
    ManageableMilestoneSection,
    MilestoneAnswerInput,
    MilestoneItemAnswer,
    MilestoneMissingItem,
    MilestoneStatus,
} from '@/types';

interface Props {
    investigationId: string;
    // Agents can edit drafts and submit; admins/employers are read-only here. The
    // server is the source of truth — this prop only controls input affordances.
    canEdit: boolean;
    // Super Admin only: show the add/remove section manager. Server enforces the
    // role too — this just gates the affordance.
    canManageSections?: boolean;
}

type LocalAnswer = {
    finding: ChecklistFinding | null;
    notes: string;
};

type LocalMilestoneState = {
    summary: string;
    details: string;
    answers: Record<string, LocalAnswer>;
};

const FINDING_LABEL: Record<ChecklistFinding, string> = {
    yes: 'Yes',
    no: 'No',
    n_a: 'N/A',
};

const FINDING_RING: Record<ChecklistFinding, string> = {
    yes: 'border-green-500 bg-green-50 text-green-700',
    no: 'border-red-500 bg-red-50 text-red-700',
    n_a: 'border-gray-400 bg-gray-50 text-gray-700',
};

const STATUS_LABEL: Record<MilestoneStatus, string> = {
    not_started: 'Pending',
    in_progress: 'In progress',
    submitted: 'Submitted',
};

const STATUS_BADGE: Record<MilestoneStatus, string> = {
    not_started: 'bg-gray-100 text-gray-600 border-gray-200',
    in_progress: 'bg-orange-50 text-[#FF6B35] border-[#FF6B35]/30',
    submitted: 'bg-green-50 text-green-700 border-green-200',
};

function answersFromItems(items: MilestoneItemAnswer[] | undefined): Record<string, LocalAnswer> {
    const map: Record<string, LocalAnswer> = {};
    if (!items) return map;
    for (const item of items) {
        map[item.itemId] = {
            finding: item.finding,
            notes: item.notes ?? '',
        };
    }
    return map;
}

function buildLocalState(milestone: InvestigationMilestone): LocalMilestoneState {
    return {
        summary: milestone.summary ?? '',
        details: milestone.details ?? '',
        answers: answersFromItems(milestone.items),
    };
}

function formatSubmittedAt(value: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export default function MilestonesPanel({ investigationId, canEdit, canManageSections = false }: Props) {
    const { data, isLoading, error } = useGetInvestigationMilestonesQuery(investigationId);
    const [saveDraft, { isLoading: isSavingDraft }] = useSaveMilestoneDraftMutation();
    const [submitMilestone, { isLoading: isSubmittingMilestone }] = useSubmitMilestoneMutation();
    const [removeSection, { isLoading: isRemovingSection }] = useRemoveMilestoneSectionMutation();
    const [addSection, { isLoading: isAddingSection }] = useAddMilestoneSectionMutation();

    const [showManage, setShowManage] = useState(false);
    // sectionId currently being added/removed, so we can spinner just that row.
    const [pendingSection, setPendingSection] = useState<string | null>(null);
    const [manageError, setManageError] = useState('');

    // Local edit buffers, keyed by sectionId so flipping between milestones in
    // the same session doesn't lose in-progress edits.
    const [local, setLocal] = useState<Record<string, LocalMilestoneState>>({});
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const [missingItems, setMissingItems] = useState<MilestoneMissingItem[]>([]);
    const [statusMessage, setStatusMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
    // Track which sections have unsaved changes so the "Save draft" button feels honest.
    const dirty = useRef<Set<string>>(new Set());

    // Hydrate from server, preserving dirty buffers so an in-flight save doesn't
    // clobber what the agent typed since.
    useEffect(() => {
        if (!data) return;
        setLocal((prev) => {
            const next: Record<string, LocalMilestoneState> = { ...prev };
            for (const m of data.milestones) {
                if (dirty.current.has(m.sectionId)) continue;
                next[m.sectionId] = buildLocalState(m);
            }
            return next;
        });
        if (!activeSectionId) {
            // Default to the first editable, non-submitted milestone, falling back to
            // the first one so the agent always sees something.
            const firstEditable = data.milestones.find((m) => m.status !== 'submitted' && m.editable);
            const fallback = firstEditable ?? data.milestones[0];
            if (fallback) setActiveSectionId(fallback.sectionId);
        }
    }, [data?.investigationId, data?.submittedMilestones, data?.totalMilestones]);

    const activeMilestone = useMemo<InvestigationMilestone | null>(() => {
        if (!data || !activeSectionId) return null;
        return data.milestones.find((m) => m.sectionId === activeSectionId) ?? null;
    }, [data, activeSectionId]);

    const activeLocal = activeSectionId ? local[activeSectionId] : undefined;

    if (isLoading) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                Couldn't load milestones for this investigation.
            </div>
        );
    }

    const updateLocal = (sectionId: string, patch: Partial<LocalMilestoneState>) => {
        setLocal((prev) => {
            const current = prev[sectionId] ?? { summary: '', details: '', answers: {} };
            return { ...prev, [sectionId]: { ...current, ...patch } };
        });
        dirty.current.add(sectionId);
        setStatusMessage(null);
    };

    const updateAnswer = (sectionId: string, itemId: string, patch: Partial<LocalAnswer>) => {
        setLocal((prev) => {
            const current = prev[sectionId] ?? { summary: '', details: '', answers: {} };
            const currentAnswer = current.answers[itemId] ?? { finding: null, notes: '' };
            return {
                ...prev,
                [sectionId]: {
                    ...current,
                    answers: { ...current.answers, [itemId]: { ...currentAnswer, ...patch } },
                },
            };
        });
        dirty.current.add(sectionId);
        setMissingItems((prev) => prev.filter((m) => m.itemId !== itemId));
        setStatusMessage(null);
    };

    const buildAnswerPayload = (state: LocalMilestoneState | undefined): MilestoneAnswerInput[] => {
        if (!state) return [];
        const out: MilestoneAnswerInput[] = [];
        for (const [itemId, a] of Object.entries(state.answers)) {
            if (a.finding === null) continue;
            out.push({ itemId, finding: a.finding, ...(a.notes.trim() ? { notes: a.notes } : {}) });
        }
        return out;
    };

    const handleSaveDraft = async () => {
        if (!activeMilestone || !activeLocal) return;
        setStatusMessage(null);
        try {
            await saveDraft({
                id: investigationId,
                sectionId: activeMilestone.sectionId,
                data: {
                    summary: activeLocal.summary,
                    details: activeLocal.details,
                    answers: buildAnswerPayload(activeLocal),
                },
            }).unwrap();
            dirty.current.delete(activeMilestone.sectionId);
            setStatusMessage({ kind: 'success', text: 'Draft saved.' });
        } catch (err: any) {
            setStatusMessage({
                kind: 'error',
                text: err?.data?.message || err?.message || 'Failed to save draft.',
            });
        }
    };

    const handleSubmit = async () => {
        if (!activeMilestone || !activeLocal) return;
        setStatusMessage(null);
        setMissingItems([]);
        const trimmedSummary = activeLocal.summary.trim();
        const trimmedDetails = activeLocal.details.trim();
        if (!trimmedSummary) {
            setStatusMessage({ kind: 'error', text: 'Summary is required before submitting this milestone.' });
            return;
        }
        if (!trimmedDetails) {
            setStatusMessage({ kind: 'error', text: 'Details are required before submitting this milestone.' });
            return;
        }
        try {
            await submitMilestone({
                id: investigationId,
                sectionId: activeMilestone.sectionId,
                data: {
                    summary: trimmedSummary,
                    details: trimmedDetails,
                    answers: buildAnswerPayload(activeLocal),
                },
            }).unwrap();
            dirty.current.delete(activeMilestone.sectionId);
            setStatusMessage({ kind: 'success', text: `${activeMilestone.title} submitted and locked.` });
        } catch (err: any) {
            const payload = err?.data;
            if (payload && Array.isArray(payload.missingItems)) {
                setMissingItems(payload.missingItems);
                setStatusMessage({
                    kind: 'error',
                    text: payload.message || 'Answer every question in this milestone before submitting.',
                });
                return;
            }
            setStatusMessage({
                kind: 'error',
                text: payload?.message || err?.message || 'Failed to submit milestone.',
            });
        }
    };

    const progressPct =
        data.totalMilestones > 0 ? Math.round((data.submittedMilestones / data.totalMilestones) * 100) : 0;

    const activeEditable = canEdit && activeMilestone?.editable === true && activeMilestone.status !== 'submitted';
    const missingSet = new Set(missingItems.map((m) => m.itemId));

    const handleToggleSection = async (section: ManageableMilestoneSection) => {
        setManageError('');
        setPendingSection(section.sectionId);
        try {
            if (section.active) {
                const submitted =
                    data.milestones.find((m) => m.sectionId === section.sectionId)?.status === 'submitted';
                const confirmMsg = submitted
                    ? `"${section.title}" has submitted work. Removing it deletes that section's summary, details and findings from this investigation. Continue?`
                    : `Remove "${section.title}" from this investigation's milestones?`;
                if (!window.confirm(confirmMsg)) {
                    setPendingSection(null);
                    return;
                }
                // If we're removing the section that's open in the editor, drop the
                // selection so the editor doesn't point at a gone section.
                if (activeSectionId === section.sectionId) setActiveSectionId(null);
                await removeSection({ id: investigationId, sectionId: section.sectionId }).unwrap();
            } else {
                await addSection({ id: investigationId, sectionId: section.sectionId }).unwrap();
            }
        } catch (err: any) {
            setManageError(err?.data?.message || err?.message || 'Failed to update sections.');
        } finally {
            setPendingSection(null);
        }
    };

    const manageBusy = isRemovingSection || isAddingSection;

    return (
        <div className="flex flex-col gap-4">
            {/* Header */}
            <div className="bg-white border border-[#e0e0e0] rounded-xl px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-bold text-[#1b1b1b]">Investigation milestones</h3>
                            {data.brief?.label && (
                                <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#FF6B35]/10 text-[#FF6B35]">
                                    {data.brief.label}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-[#4e4d4d] mt-0.5">
                            {data.brief?.summary ??
                                'Work through each section, submit it to lock the findings, then send the final report.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-xs text-[#4e4d4d]">
                            <span className="font-semibold text-[#1b1b1b]">{data.submittedMilestones}</span> /{' '}
                            {data.totalMilestones} submitted
                        </div>
                        {canManageSections && (
                            <button
                                type="button"
                                onClick={() => {
                                    setShowManage((v) => !v);
                                    setManageError('');
                                }}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                                    showManage
                                        ? 'bg-[#151515] text-white border-[#151515]'
                                        : 'bg-white text-[#1b1b1b] border-[#e0e0e0] hover:bg-gray-50'
                                }`}
                            >
                                <SlidersHorizontal className="w-3.5 h-3.5" />
                                Manage sections
                            </button>
                        )}
                    </div>
                </div>
                <div className="h-1.5 w-full bg-[#f0f0f0] rounded-full overflow-hidden">
                    <div
                        className="h-full bg-[#FF6B35] transition-all duration-300"
                        style={{ width: `${progressPct}%` }}
                    />
                </div>
            </div>

            {/* Super-admin section manager — add or remove milestone sections for this case. */}
            {canManageSections && showManage && (
                <div className="bg-white border border-[#e0e0e0] rounded-xl px-5 py-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-bold text-[#1b1b1b]">Manage milestone sections</h4>
                        <p className="text-xs text-[#4e4d4d] mt-0.5">
                            Add or remove sections for this investigation. Removing a section deletes any work saved
                            against it and drops it from the checklist, compiled report and employer progress.
                        </p>
                    </div>
                    {manageError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700 mb-3">
                            {manageError}
                        </div>
                    )}
                    <ul className="flex flex-col divide-y divide-[#f0f0f0]">
                        {(data.manageableSections ?? []).map((section) => {
                            const rowBusy = pendingSection === section.sectionId && manageBusy;
                            return (
                                <li key={section.sectionId} className="flex items-center justify-between gap-3 py-2.5">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-[#1b1b1b] truncate">{section.title}</p>
                                        <p className="text-[11px] text-[#717680]">
                                            {section.itemCount} item{section.itemCount === 1 ? '' : 's'}
                                            {!section.inCheckTypeScope && (
                                                <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-[#FF6B35]/10 text-[#FF6B35] font-semibold uppercase tracking-wider text-[10px]">
                                                    Extra
                                                </span>
                                            )}
                                            {section.active ? '' : ' · removed'}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={manageBusy}
                                        onClick={() => handleToggleSection(section)}
                                        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                            section.active
                                                ? 'bg-white text-red-600 border-red-200 hover:bg-red-50'
                                                : 'bg-white text-green-700 border-green-200 hover:bg-green-50'
                                        }`}
                                    >
                                        {rowBusy ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        ) : section.active ? (
                                            <Trash2 className="w-3.5 h-3.5" />
                                        ) : (
                                            <Plus className="w-3.5 h-3.5" />
                                        )}
                                        {section.active ? 'Remove' : 'Add'}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {!canEdit && !canManageSections && (
                <div className="bg-[#fafafa] border border-[#e9eaeb] rounded-xl px-4 py-3 text-xs text-[#4e4d4d]">
                    Read-only view — only the assigned agent can edit and submit milestones.
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-4">
                {/* Stepper */}
                <div className="lg:w-72 flex-shrink-0">
                    <ol className="flex flex-col gap-2">
                        {data.milestones.map((m) => {
                            const isActive = m.sectionId === activeSectionId;
                            const stepperLocal = isActive ? activeLocal : undefined;
                            const localAnswered = stepperLocal
                                ? Object.values(stepperLocal.answers).filter((a) => a.finding !== null).length
                                : m.answeredCount;
                            return (
                                <li key={m.sectionId}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setActiveSectionId(m.sectionId);
                                            setStatusMessage(null);
                                            setMissingItems([]);
                                        }}
                                        className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                                            isActive
                                                ? 'border-[#FF6B35] bg-[#FFF8F4]'
                                                : 'border-[#e0e0e0] bg-white hover:bg-[#fafafa]'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#151515] text-white text-[11px] font-bold">
                                                {m.order}
                                            </span>
                                            <span className="text-sm font-semibold text-[#1b1b1b] truncate">
                                                {m.title}
                                            </span>
                                            {m.status === 'submitted' && (
                                                <Lock className="w-3 h-3 text-green-600 ml-auto" />
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span
                                                className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${STATUS_BADGE[m.status]}`}
                                            >
                                                {STATUS_LABEL[m.status]}
                                            </span>
                                            <span className="text-[11px] text-[#717680]">
                                                {localAnswered} / {m.itemCount} answered
                                            </span>
                                        </div>
                                        {m.submittedAt && (
                                            <p className="text-[11px] text-[#717680] mt-1">
                                                Submitted {formatSubmittedAt(m.submittedAt)}
                                            </p>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </div>

                {/* Editor */}
                <div className="flex-1 min-w-0">
                    {activeMilestone ? (
                        <div className="bg-white border border-[#e0e0e0] rounded-xl overflow-hidden">
                            <div className="border-b border-[#f0f0f0] px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <p className="text-[11px] uppercase tracking-wider text-[#717680] font-semibold">
                                        Milestone {activeMilestone.order} of {data.totalMilestones}
                                    </p>
                                    <h4 className="text-base font-bold text-[#1b1b1b] truncate">
                                        {activeMilestone.title}
                                    </h4>
                                </div>
                                <span
                                    className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${STATUS_BADGE[activeMilestone.status]}`}
                                >
                                    {STATUS_LABEL[activeMilestone.status]}
                                </span>
                            </div>

                            {activeMilestone.status === 'submitted' && (
                                <div className="bg-green-50 border-b border-green-100 px-5 py-3 text-xs text-green-700 flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Locked, submitted {formatSubmittedAt(activeMilestone.submittedAt)}. Ask an admin if
                                    it needs to be reopened.
                                </div>
                            )}

                            {missingItems.length > 0 && (
                                <div className="bg-red-50 border-b border-red-200 px-5 py-3 text-sm text-red-700">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-semibold mb-1">
                                                {missingItems.length} item{missingItems.length === 1 ? '' : 's'} still
                                                need an answer
                                            </p>
                                            <ul className="list-disc list-inside text-xs space-y-0.5">
                                                {missingItems.slice(0, 6).map((m) => (
                                                    <li key={m.itemId}>{m.question}</li>
                                                ))}
                                                {missingItems.length > 6 && (
                                                    <li className="text-[11px] text-red-600">
                                                        + {missingItems.length - 6} more
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {statusMessage && (
                                <div
                                    className={`px-5 py-2 text-xs border-b ${
                                        statusMessage.kind === 'success'
                                            ? 'bg-green-50 border-green-100 text-green-700'
                                            : 'bg-red-50 border-red-100 text-red-700'
                                    }`}
                                >
                                    {statusMessage.text}
                                </div>
                            )}

                            <div className="px-5 py-4 flex flex-col gap-5">
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-semibold text-[#1b1b1b]">
                                        Summary <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={activeLocal?.summary ?? ''}
                                        onChange={(e) =>
                                            updateLocal(activeMilestone.sectionId, { summary: e.target.value })
                                        }
                                        disabled={!activeEditable}
                                        rows={2}
                                        placeholder="Short one-line conclusion for this section."
                                        className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm text-[#1b1b1b] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    />
                                </div>

                                <div className="flex flex-col gap-2">
                                    <label className="text-xs font-semibold text-[#1b1b1b]">
                                        Details <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={activeLocal?.details ?? ''}
                                        onChange={(e) =>
                                            updateLocal(activeMilestone.sectionId, { details: e.target.value })
                                        }
                                        disabled={!activeEditable}
                                        rows={6}
                                        placeholder="Long-form notes: what you did, where you went, what you found."
                                        className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm text-[#1b1b1b] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    />
                                </div>

                                <div>
                                    <p className="text-xs font-semibold text-[#1b1b1b] mb-2">
                                        Checklist items{' '}
                                        <span className="font-normal text-[#717680]">
                                            ({activeMilestone.items?.length ?? activeMilestone.itemCount} questions)
                                        </span>
                                    </p>
                                    <div className="border border-[#f0f0f0] rounded-lg overflow-hidden">
                                        {(activeMilestone.items ?? []).map((item) => {
                                            const answer = activeLocal?.answers[item.itemId] ?? {
                                                finding: null,
                                                notes: '',
                                            };
                                            return (
                                                <div
                                                    key={item.itemId}
                                                    className={`px-4 py-3 border-b border-[#f0f0f0] last:border-b-0 ${
                                                        missingSet.has(item.itemId) ? 'bg-red-50' : ''
                                                    }`}
                                                >
                                                    <p className="text-sm text-[#1b1b1b] leading-snug mb-2">
                                                        {item.question}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        {(['yes', 'no', 'n_a'] as ChecklistFinding[]).map((f) => {
                                                            const active = answer.finding === f;
                                                            return (
                                                                <button
                                                                    key={f}
                                                                    type="button"
                                                                    disabled={!activeEditable}
                                                                    onClick={() =>
                                                                        updateAnswer(
                                                                            activeMilestone.sectionId,
                                                                            item.itemId,
                                                                            { finding: f },
                                                                        )
                                                                    }
                                                                    className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                                                                        active
                                                                            ? FINDING_RING[f]
                                                                            : 'border-[#e0e0e0] bg-white text-[#4e4d4d] hover:bg-gray-50'
                                                                    } ${!activeEditable ? 'cursor-not-allowed opacity-70' : ''}`}
                                                                >
                                                                    {FINDING_LABEL[f]}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <textarea
                                                        value={answer.notes}
                                                        onChange={(e) =>
                                                            updateAnswer(activeMilestone.sectionId, item.itemId, {
                                                                notes: e.target.value,
                                                            })
                                                        }
                                                        disabled={!activeEditable}
                                                        rows={2}
                                                        placeholder="Notes (optional)"
                                                        className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2 text-xs text-[#1b1b1b] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y disabled:bg-gray-50 disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {activeEditable && (
                                    <div className="flex items-center justify-end gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={handleSaveDraft}
                                            disabled={isSavingDraft || isSubmittingMilestone}
                                            className="px-4 py-2 rounded-lg text-sm font-medium border border-[#d5d7da] text-[#1b1b1b] hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            {isSavingDraft ? 'Saving…' : 'Save draft'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={isSavingDraft || isSubmittingMilestone}
                                            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#FF6B35] hover:bg-[#e05a2a] text-white disabled:opacity-50"
                                        >
                                            {isSubmittingMilestone ? 'Submitting…' : 'Submit milestone'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white border border-[#e0e0e0] rounded-xl px-5 py-8 text-center text-sm text-[#717680]">
                            Select a milestone on the left to start.
                        </div>
                    )}
                </div>
            </div>

            {data.allSubmitted && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    All milestones submitted. You can now send the final report for review.
                </div>
            )}
        </div>
    );
}
